import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import RequireRootRole from './components/RequireRootRole';
import SqlErrorsPanel from './SqlErrorsPanel';
import { PageContainer, Section } from '../../components/AdminContentWrapper';
import { ADMIN_COLORS, containedButtonSx, darkFieldSx, outlinedButtonSx } from '../../components/adminStyles';
import ConfirmDestructiveDialog from '../../../../components/common/ConfirmDestructiveDialog';
import SqlRulesService from '../../../../services/SqlRulesService';

// Mismas reglas que sql_rules.validate_rules (backend): aquí solo para
// avisar antes de guardar; el backend vuelve a validar y responde 400.
export const rulesProblem = (text, maxChars) => {
  if (!text.trim()) return 'Las reglas no pueden quedar vacías. Para volver a las de fábrica usa "Restaurar predeterminadas".';
  if (maxChars && text.trim().length > maxChars) return `Máximo ${maxChars} caracteres.`;
  return null;
};

const monoFieldSx = {
  ...darkFieldSx,
  '& .MuiOutlinedInput-input': { fontFamily: 'monospace', fontSize: '0.85rem' }
};

const defaultsBoxSx = {
  mt: 2,
  p: 2,
  borderRadius: 1,
  border: `1px solid ${ADMIN_COLORS.border}`,
  backgroundColor: ADMIN_COLORS.surface,
  color: ADMIN_COLORS.text,
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  maxHeight: 320,
  overflowY: 'auto'
};

const chipSx = (custom) => ({
  fontWeight: 600,
  backgroundColor: custom ? ADMIN_COLORS.selectedBg : 'transparent',
  color: custom ? ADMIN_COLORS.accent : ADMIN_COLORS.muted,
  border: `1px solid ${custom ? ADMIN_COLORS.accent : ADMIN_COLORS.borderStrong}`
});

const SqlRulesEditor = () => {
  const [data, setData] = useState(null);
  // Borradores por motor: cambiar de motor no pierde lo que se estaba escribiendo.
  const [drafts, setDrafts] = useState({});
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [showDefaults, setShowDefaults] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Aplica la respuesta del backend: conserva los borradores que siguen
  // siendo distintos de lo guardado.
  const applyData = useCallback((next) => {
    setData(next);
    setDrafts((prev) => Object.fromEntries(Object.entries(prev).filter(([id, text]) => {
      const engine = next.engines.find((item) => item.id === id);
      return engine && engine.rules !== text;
    })));
    setSelected((prev) => prev || next.engines[0]?.id || null);
  }, []);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      applyData(await SqlRulesService.getRules());
      setConflict(false);
    } catch (err) {
      setError(err.message || 'No fue posible obtener las reglas.');
    } finally {
      setIsLoading(false);
    }
  }, [applyData]);

  useEffect(() => {
    load();
  }, [load]);

  const engine = data?.engines.find((item) => item.id === selected);
  const text = engine ? (drafts[engine.id] ?? engine.rules) : '';
  const isDirty = Boolean(engine) && drafts[engine.id] !== undefined && drafts[engine.id] !== engine.rules;
  const problem = engine ? rulesProblem(text, data.max_chars) : null;
  const otherDirty = useMemo(
    () => (data?.engines || []).filter((item) => item.id !== selected && drafts[item.id] !== undefined && drafts[item.id] !== item.rules),
    [data, drafts, selected]
  );

  const setText = (value) => setDrafts((prev) => ({ ...prev, [engine.id]: value }));
  const discard = () => setDrafts(({ [engine.id]: _removed, ...rest }) => rest);

  const handleFailure = (err, fallback) => {
    if (err.status === 409) setConflict(true);
    setSnackbar({ open: true, severity: 'error', message: err.message || fallback });
  };

  const handleSave = async () => {
    if (problem) return;
    try {
      setIsSaving(true);
      applyData(await SqlRulesService.saveRules(engine.id, text, data.version));
      setSnackbar({ open: true, severity: 'success', message: `Reglas de ${engine.label} guardadas. Aplican desde la siguiente pregunta.` });
    } catch (err) {
      handleFailure(err, 'No se pudieron guardar las reglas.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const next = await SqlRulesService.resetRules(engine.id, data.version);
      discard();
      applyData(next);
      setConfirmReset(false);
      setSnackbar({ open: true, severity: 'success', message: `${engine.label} vuelve a usar las reglas de fábrica.` });
    } catch (err) {
      setConfirmReset(false);
      handleFailure(err, 'No se pudieron restaurar las reglas.');
    }
  };

  return (
    <PageContainer
      title="Reglas SQL por motor"
      description="Lo que el agente debe respetar al escribir consultas en cada tipo de base de datos: qué no acepta su dialecto y qué usar en su lugar. Aplican al chat, al Análisis Profundo y a la Generación de tableros de los agentes que consultan ese motor, desde la siguiente pregunta y sin desplegar nada."
      actions={(
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading || isSaving} sx={outlinedButtonSx}>
          Actualizar
        </Button>
      )}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      {conflict && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={<Button color="inherit" size="small" onClick={load}>Recargar</Button>}
        >
          Otra persona cambió las reglas mientras las editabas. Recarga para ver la versión actual; tus cambios sin guardar se conservan.
        </Alert>
      )}

      {isLoading && !data ? (
        !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: ADMIN_COLORS.accent }} />
          </Box>
        )
      ) : engine && (
        <Section
          title="Tipo de base de datos"
          subtitle="Elige el motor para ver y editar sus reglas. El agente solo recibe las reglas de los motores que tiene conectados."
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
            <TextField
              select
              id="sql-rules-engine"
              label="Motor"
              value={engine.id}
              onChange={(event) => { setSelected(event.target.value); setShowDefaults(false); }}
              size="small"
              sx={{ ...darkFieldSx, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 280 } }}
            >
              {data.engines.map((item) => {
                const dirty = drafts[item.id] !== undefined && drafts[item.id] !== item.rules;
                return (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                    {item.custom ? ' · personalizadas' : ''}
                    {dirty ? ' · sin guardar' : ''}
                  </MenuItem>
                );
              })}
            </TextField>
            <Chip size="small" label={engine.custom ? 'Personalizadas' : 'De fábrica'} sx={chipSx(engine.custom)} />
          </Stack>

          <Typography sx={{ color: ADMIN_COLORS.text, mb: 0.5 }}>{engine.description}</Typography>
          <Typography sx={{ color: ADMIN_COLORS.muted, mb: 2, fontSize: '0.85rem' }}>
            Herramienta del agente: <Box component="span" sx={{ fontFamily: 'monospace' }}>{engine.tool}</Box>
          </Typography>

          {otherDirty.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Tienes cambios sin guardar en: {otherDirty.map((item) => item.label).join(', ')}.
            </Alert>
          )}

          <TextField
            value={text}
            onChange={(event) => setText(event.target.value)}
            multiline
            minRows={14}
            maxRows={24}
            fullWidth
            error={Boolean(problem)}
            helperText={problem || `${text.trim().length} / ${data.max_chars} caracteres. Una regla por línea; se agregan al prompt tal cual.`}
            inputProps={{ 'aria-label': `Reglas de ${engine.label}` }}
            sx={monoFieldSx}
          />

          {engine.custom && (
            <>
              <Button
                size="small"
                onClick={() => setShowDefaults((prev) => !prev)}
                sx={{ ...outlinedButtonSx, mt: 1, border: 'none', px: 0 }}
              >
                {showDefaults ? 'Ocultar las reglas de fábrica' : 'Ver las reglas de fábrica'}
              </Button>
              {showDefaults && (
                <Box sx={defaultsBoxSx} aria-label={`Reglas de fábrica de ${engine.label}`}>{engine.default_rules}</Box>
              )}
            </>
          )}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Typography sx={{ color: ADMIN_COLORS.muted }}>
              {engine.custom && engine.updated_at
                ? `Última actualización: ${engine.updated_at}${engine.updated_by ? ` por ${engine.updated_by}` : ''}`
                : 'Se usan las reglas de fábrica de la plataforma.'}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexShrink: 0 }}>
              <Button variant="outlined" onClick={discard} disabled={!isDirty || isSaving} sx={outlinedButtonSx}>
                Descartar cambios
              </Button>
              <Button
                variant="outlined"
                startIcon={<RestoreIcon />}
                onClick={() => setConfirmReset(true)}
                disabled={!engine.custom || isSaving}
                sx={{ ...outlinedButtonSx, whiteSpace: 'nowrap' }}
              >
                Restaurar predeterminadas
              </Button>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!isDirty || Boolean(problem) || isSaving}
                sx={containedButtonSx}
              >
                {isSaving ? 'Guardando…' : 'Guardar'}
              </Button>
            </Stack>
          </Stack>
        </Section>
      )}

      {engine && <SqlErrorsPanel engine={engine} />}

      <ConfirmDestructiveDialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={handleReset}
        title={`Restaurar las reglas de ${engine?.label || ''}`}
        confirmLabel="Restaurar"
        progressMessage="Restaurando…"
        checkboxLabel="Entiendo que se perderán las reglas personalizadas de este motor."
      >
        <Typography>
          El motor volverá a usar las reglas de fábrica de la plataforma desde la siguiente pregunta.
        </Typography>
      </ConfirmDestructiveDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            color: 'var(--wb-on-primary)',
            backgroundColor: snackbar.severity === 'success' ? 'var(--wb-success-90)' : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

const SqlRulesSettings = () => (
  <RequireRootRole title="Reglas SQL por motor">
    <SqlRulesEditor />
  </RequireRootRole>
);

export default SqlRulesSettings;
